import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import FlagCallModal from './FlagCallModal';

const CallHistory = ({ currentUser, filterContactName, onBack }) => {
  const [calls, setCalls] = useState([]);
  const [expandedCall, setExpandedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  // 2026-04-26: surface query failures (esp. Firestore missing-index errors)
  // visibly rather than swallowing with console.error only. The 2026-04-26
  // empty-call-history bug went undetected for hours because the catch
  // block here just logged to console and rendered an empty list. Loud is
  // better than silent.
  const [loadError, setLoadError] = useState(null);
  // Phase 4 + USER_FLAGGED_CALL_IMPROVEMENT v1 — flag-this-call state.
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagTarget, setFlagTarget] = useState(null); // { id, contact_name }
  const [flagToast, setFlagToast] = useState(null);   // string | null

  const openFlagModal = (call) => {
    setFlagTarget({ id: call.id, contact_name: call.contact_name || 'Unknown' });
    setFlagModalOpen(true);
  };
  const closeFlagModal = () => {
    setFlagModalOpen(false);
    setFlagTarget(null);
  };
  const onFlagged = () => {
    setFlagToast("Thanks — your flag was submitted. The Maia team will use it to improve.");
  };

  useEffect(() => {
    if (currentUser) {
      const fetchCallHistory = async () => {
        setLoadError(null);
        try {
          const historyRef = collection(db, "users", currentUser.uid, "call_history");
          const q = query(historyRef, orderBy("timestamp", "desc"));
          const snapshot = await getDocs(q);
          let callList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // Filter by contact name if provided
          if (filterContactName) {
            callList = callList.filter(c => c.contact_name === filterContactName);
          }

          setCalls(callList);
        } catch (error) {
          console.error("Error fetching call history:", error);
          // Surface the failure to the user. Firestore missing-index errors
          // come back with code 'failed-precondition' — give those a more
          // actionable message; everything else gets a generic "couldn't
          // load" so the user at least knows something went wrong.
          const isMissingIndex = error?.code === 'failed-precondition';
          setLoadError(
            isMissingIndex
              ? "Couldn't load your call history (Firestore index missing — please report this)."
              : "Couldn't load your call history. Please try again."
          );
        } finally {
          setLoading(false);
        }
      };

      fetchCallHistory();
    }
  }, [currentUser, filterContactName]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getOutcomeColor = (outcome) => {
    if (!outcome) return '#666';
    const lower = outcome.toLowerCase();
    if (lower.includes('callback') || lower.includes('confirmed')) return '#388e3c';
    if (lower.includes('handoff') || lower.includes('connected')) return '#1976d2';
    if (lower.includes('ended')) return '#666';
    return '#666';
  };

  const getOutcomeLabel = (outcome) => {
    if (!outcome) return 'Call ended';
    if (outcome.includes('Callback')) return 'Callback requested';
    if (outcome.includes('handoff')) return 'Handed off to you';
    if (outcome.includes('ended')) return 'Call ended';
    return outcome.substring(0, 40);
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#999' }}>Loading call history...</div>;
  }

  return (
    <div>
      {/* Header with back button if filtering by contact */}
      {(onBack || filterContactName) && (
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '10px' }}
          onClick={onBack}
        >
          <ArrowBackIcon />
          <Typography variant="subtitle1" color="primary" style={{ marginLeft: '5px' }}>
            {filterContactName ? `Back to Contacts` : 'Back'}
          </Typography>
        </div>
      )}

      {filterContactName && (
        <Typography variant="h6" style={{ marginBottom: '10px' }}>
          Call History — {filterContactName}
        </Typography>
      )}

      {/* Visible error banner — replaces the silent console.error swallow
          that hid the 2026-04-26 missing-index regression. */}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {calls.length === 0 ? (
        <div style={{ padding: '20px', color: '#999', textAlign: 'center' }}>
          {filterContactName ? `No call history for ${filterContactName}` : 'No call history yet'}
        </div>
      ) : (
        <List>
          {calls.map((call) => (
            <div key={call.id} style={{ marginBottom: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              {/* Call summary row */}
              <ListItem
                button
                onClick={() => setExpandedCall(expandedCall === call.id ? null : call.id)}
                style={{ backgroundColor: expandedCall === call.id ? '#f5f5f5' : 'white' }}
              >
                <ListItemText
                  primary={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>{call.contact_name || 'Unknown'}</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>{formatDate(call.timestamp)}</span>
                    </div>
                  }
                  secondary={
                    <div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                        {call.instructions || 'get live agent'}
                      </div>
                      {call.summary ? (
                        // Phase 4b: LLM-generated 1-3 sentence summary is the default view.
                        // Older call_history docs (pre-Phase-4) don't have this field;
                        // we fall back to showing only the outcome label in that case.
                        <div style={{
                          fontSize: '14px',
                          color: '#222',
                          marginTop: '6px',
                          lineHeight: '1.5',
                        }}>
                          {call.summary}
                        </div>
                      ) : null}
                      <div style={{ fontSize: '12px', color: getOutcomeColor(call.outcome), marginTop: '4px', fontWeight: 'bold' }}>
                        {getOutcomeLabel(call.outcome)}
                      </div>
                    </div>
                  }
                />
                <IconButton size="small">
                  {expandedCall === call.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </ListItem>

              {/* Phase C of USER_FLAGGED_CALL_IMPROVEMENT — small,
                  always-visible "Help improve Maia" button per call row.
                  Opens the consent modal; on submit, /flag-call writes a
                  redacted copy of this call to flagged_calls. */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '4px 12px 8px',
                backgroundColor: expandedCall === call.id ? '#f5f5f5' : 'white',
              }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => { e.stopPropagation(); openFlagModal(call); }}
                  sx={{ textTransform: 'none', fontSize: '12px', color: '#666' }}
                >
                  Help improve Maia
                </Button>
              </div>

              {/* Expanded transcript — full turn-by-turn detail behind the summary */}
              {expandedCall === call.id && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fafafa',
                  borderTop: '1px solid #e0e0e0',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px',
                  }}>
                    Full transcript
                  </div>
                  {(call.progress_log || []).map((entry, idx) => (
                    <div key={idx} style={{ marginBottom: '4px' }}>
                      {entry.role === 'contact' ? (
                        <div><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Contact:</span> {entry.text}</div>
                      ) : (
                        <div><span style={{ color: '#388e3c', fontWeight: 'bold' }}>Maia:</span> {entry.text}</div>
                      )}
                    </div>
                  ))}
                  {(!call.progress_log || call.progress_log.length === 0) && (
                    <div style={{ color: '#999', fontStyle: 'italic' }}>No transcript available</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </List>
      )}

      {/* Flag-this-call modal — opened from any call row's "Help improve Maia" button. */}
      <FlagCallModal
        open={flagModalOpen}
        onClose={closeFlagModal}
        currentUser={currentUser}
        callHistoryId={flagTarget?.id}
        contactName={flagTarget?.contact_name}
        onFlagged={onFlagged}
      />

      {/* Confirmation toast on successful flag */}
      <Snackbar
        open={Boolean(flagToast)}
        autoHideDuration={5000}
        onClose={() => setFlagToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setFlagToast(null)} sx={{ width: '100%' }}>
          {flagToast}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CallHistory;
